/**
 * Form dropdown options for Prospect Desk forms
 * These options are used across CreateNewProspect and Prospect Details forms
 */

import type { BadgeProps } from '@lucid/lego';
import { isMy27Enabled } from '../../config/featureFlags';
import { ProspectActivityType, type Prospect } from '../../api/services/lupp/prospect/dto.prospect';

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

/**
 * i18n Configuration
 * List of constant variables that should be included for translation.
 * Brand names, product names, and identifiers should NOT be included.
 */
export const I18N_TRANSLATABLE_CONSTANTS = [
  'titleOptions',
  'purchaseTypeOptions',
  'financeTypeOptions',
  'purchaseTimingOptions',
  'preferredContactMethodOptions',
  'emailConsentOptions',
  'phoneConsentOptions',
  'testDriveTimeOptions',
  'statusOptions',
] as const;

/**
 * Car manufacturer options
 */
export const carMakerOptions = [
  { label: 'Acura', value: 'ACURA' },
  { label: 'Aiways', value: 'AIWAYS' },
  { label: 'Alfa Romeo', value: 'ALFA ROMEO' },
  { label: 'Aston Martin', value: 'ASTON MARTIN' },
  { label: 'Audi', value: 'AUDI' },
  { label: 'Bentley', value: 'BENTLEY' },
  { label: 'BMW', value: 'BMW' },
  { label: 'BMW Alpina', value: 'BMW-ALPINA' },
  { label: 'Bugatti', value: 'BUGATTI' },
  { label: 'Buick', value: 'BUICK' },
  { label: 'BYD', value: 'BYD' },
  { label: 'Cadillac', value: 'CADILLAC' },
  { label: 'Chevrolet', value: 'CHEVROLET' },
  { label: 'Chrysler', value: 'CHRYSLER' },
  { label: 'Citroen', value: 'CITROEN' },
  { label: 'Cupra', value: 'CUPRA' },
  { label: 'Dacia', value: 'DACIA' },
  { label: 'Daewoo', value: 'DAEWOO' },
  { label: 'Did not Collect', value: 'Did not Collect' },
  { label: 'Dodge', value: 'DODGE' },
  { label: 'DS', value: 'DS' },
  { label: 'Ferrari', value: 'FERRARI' },
  { label: 'Fiat', value: 'FIAT' },
  { label: 'Fisker', value: 'FISKER' },
  { label: 'Ford', value: 'FORD' },
  { label: 'Genesis', value: 'GENESIS' },
  { label: 'GMC', value: 'GMC' },
  { label: 'Honda', value: 'HONDA' },
  { label: 'Hongqi', value: 'HONGQI' },
  { label: 'Hyundai', value: 'HYUNDAI' },
  { label: 'Infiniti', value: 'INFINITI' },
  { label: 'Jaguar', value: 'JAGUAR' },
  { label: 'Jeep', value: 'JEEP' },
  { label: 'Kia', value: 'KIA' },
  { label: 'Koenigsegg', value: 'KOENIGSEGG' },
  { label: 'KTM', value: 'KTM' },
  { label: 'Lamborghini', value: 'LAMBORGHINI' },
  { label: 'Lancia', value: 'LANCIA' },
  { label: 'Land Rover', value: 'LAND ROVER' },
  { label: 'Lexus', value: 'LEXUS' },
  { label: 'Lincoln', value: 'LINCOLN' },
  { label: 'Lotus', value: 'LOTUS' },
  { label: 'Lucid', value: 'LUCID' },
  { label: 'Lynk & Co', value: 'LYNK & CO' },
  { label: 'Maserati', value: 'MASERATI' },
  { label: 'Maybach', value: 'MAYBACH' },
  { label: 'Mazda', value: 'MAZDA' },
  { label: 'McLaren', value: 'MCLAREN' },
  { label: 'Mercedes-Benz', value: 'MERCEDES-BENZ' },
  { label: 'MG', value: 'MG' },
  { label: 'Mini', value: 'MINI' },
  { label: 'Mitsubishi', value: 'MITSUBISHI' },
  { label: 'Nio', value: 'NIO' },
  { label: 'Nissan', value: 'NISSAN' },
  { label: 'Opel', value: 'OPEL' },
  { label: 'Other', value: 'OTHER' },
  { label: 'Peugeot', value: 'PEUGEOT' },
  { label: 'Polestar', value: 'POLESTAR' },
  { label: 'Porsche', value: 'PORSCHE' },
  { label: 'Ram', value: 'RAM' },
  { label: 'Renault', value: 'RENAULT' },
  { label: 'Rimac', value: 'RIMAC' },
  { label: 'Rivian', value: 'RIVIAN' },
  { label: 'Rolls Royce', value: 'ROLLS ROYCE' },
  { label: 'Saab', value: 'SAAB' },
  { label: 'Seat', value: 'SEAT' },
  { label: 'Skoda', value: 'SKODA' },
  { label: 'Smart', value: 'SMART' },
  { label: 'Subaru', value: 'SUBARU' },
  { label: 'Suzuki', value: 'SUZUKI' },
  { label: 'Tesla', value: 'TESLA' },
  { label: 'Toyota', value: 'TOYOTA' },
  { label: 'VinFast', value: 'VINFAST' },
  { label: 'Volkswagen', value: 'VOLKSWAGEN' },
  { label: 'Volvo', value: 'VOLVO' },
  { label: 'Voyah', value: 'VOYAH' },
  { label: 'Xpeng', value: 'XPENG' },
  { label: 'Zeekr', value: 'ZEEKR' },
];

export { getTitleOptions } from '../../constants/constants';

import { getTitleOptions as _getTitleOptions } from '../../constants/constants';

/** @deprecated Use getTitleOptions(labels, locale) instead */
export const titleOptions = _getTitleOptions();

/**
 * Lucid vehicle model options
 */
export const interestedModelOptions = [
  { label: 'Lucid Air Pure', value: 'Lucid Air Pure' },
  { label: 'Lucid Air Touring', value: 'Lucid Air Touring' },
  { label: 'Lucid Air Grand Touring', value: 'Lucid Air Grand Touring' },
  { label: 'Lucid Air Dream Edition', value: 'Lucid Air Dream Edition' },
  { label: 'Lucid Air Sapphire', value: 'Lucid Air Sapphire' },
  { label: 'Lucid Gravity Touring', value: 'Lucid Gravity Touring' },
  { label: 'Lucid Gravity Touring Plus', value: 'Lucid Gravity Touring Plus' },
  { label: 'Lucid Gravity Grand Touring', value: 'Lucid Gravity Grand Touring' },
  { label: 'Lucid Gravity Grand Touring Ultimate', value: 'Lucid Gravity Grand Touring Ultimate' },
];

/** MY27-only Gravity trims, hidden from model/trim dropdowns when the MY27 flag is disabled. */
const MY27_INTERESTED_MODELS = new Set([
  'Lucid Gravity Touring Plus',
  'Lucid Gravity Grand Touring Ultimate',
]);

/**
 * Interested model/trim options, gated by the MY27 feature flag.
 * When MY27 is disabled, the new Gravity trims (Touring Plus, Grand Touring Ultimate) are hidden.
 * @returns Model/trim options filtered by the MY27 feature flag
 */
export const getInterestedModelOptions = () =>
  isMy27Enabled()
    ? interestedModelOptions
    : interestedModelOptions.filter((opt) => !MY27_INTERESTED_MODELS.has(opt.value));

/**
 * Purchase type options
 * @param labels - i18n labels from Contentful
 */
export const getPurchaseTypeOptions = (labels?: Labels) => [
  { label: labels?.['const.purchaseTypeOptions.private'] ?? 'Private', value: 'Private' },
  { label: labels?.['const.purchaseTypeOptions.business'] ?? 'Business', value: 'Business' },
];

/** @deprecated Use getPurchaseTypeOptions(labels) instead */
export const purchaseTypeOptions = getPurchaseTypeOptions();

/**
 * Finance type options
 * @param labels - i18n labels from Contentful
 */
export const getFinanceTypeOptions = (labels?: Labels) => [
  { label: labels?.['const.financeTypeOptions.cash'] ?? 'Cash', value: 'Cash' },
  { label: labels?.['const.financeTypeOptions.lease'] ?? 'Lease', value: 'Lease' },
  { label: labels?.['const.financeTypeOptions.loan'] ?? 'Loan', value: 'Loan' },
  {
    label: labels?.['const.financeTypeOptions.thirdPartyFinancing'] ?? '3rd Party Financing',
    value: 'Self-arranged Finance',
  },
  { label: labels?.['const.financeTypeOptions.undecided'] ?? 'Undecided', value: 'Undecided' },
];

/** @deprecated Use getFinanceTypeOptions(labels) instead */
export const financeTypeOptions = getFinanceTypeOptions();

/**
 * Purchase timing options
 * @param labels - i18n labels from Contentful
 * @remarks Updated per AUCTUS-1182 - Aligned with Salesforce timing values
 */
const QUARTER_VALUE_PATTERN = /^Q([1-4]) (\d{2})$/;

/**
 * Returns true when a `Q{n} {yy}` value is the current quarter or later.
 * Non-quarter values (e.g. "Did not Collect", "Immediate") always pass.
 */
const isCurrentOrFutureQuarter = (value: string, now: Date = new Date()): boolean => {
  const match = QUARTER_VALUE_PATTERN.exec(value);
  if (!match) return true;
  const quarter = Number(match[1]);
  const year = 2000 + Number(match[2]);
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const currentYear = now.getFullYear();
  return year > currentYear || (year === currentYear && quarter >= currentQuarter);
};

export const getPurchaseTimingOptions = (labels?: Labels) =>
  [
    {
      label: labels?.['const.purchaseTimingOptions.didNotCollect'] ?? 'Did not Collect',
      value: 'Did not Collect',
    },
    { label: labels?.['const.purchaseTimingOptions.q1-26'] ?? 'Q1 26', value: 'Q1 26' },
    { label: labels?.['const.purchaseTimingOptions.q2-26'] ?? 'Q2 26', value: 'Q2 26' },
    { label: labels?.['const.purchaseTimingOptions.q3-26'] ?? 'Q3 26', value: 'Q3 26' },
    { label: labels?.['const.purchaseTimingOptions.q4-26'] ?? 'Q4 26', value: 'Q4 26' },
    { label: labels?.['const.purchaseTimingOptions.q1-27'] ?? 'Q1 27', value: 'Q1 27' },
    { label: labels?.['const.purchaseTimingOptions.q2-27'] ?? 'Q2 27', value: 'Q2 27' },
    { label: labels?.['const.purchaseTimingOptions.q3-27'] ?? 'Q3 27', value: 'Q3 27' },
    { label: labels?.['const.purchaseTimingOptions.q4-27'] ?? 'Q4 27', value: 'Q4 27' },
    { label: labels?.['const.purchaseTimingOptions.q1-28'] ?? 'Q1 28', value: 'Q1 28' },
    { label: labels?.['const.purchaseTimingOptions.q2-28'] ?? 'Q2 28', value: 'Q2 28' },
    { label: labels?.['const.purchaseTimingOptions.q3-28'] ?? 'Q3 28', value: 'Q3 28' },
    { label: labels?.['const.purchaseTimingOptions.q4-28'] ?? 'Q4 28', value: 'Q4 28' },
    {
      label: labels?.['const.purchaseTimingOptions.immediate'] ?? 'Immediate',
      value: 'Immediate',
    },
  ].filter((option) => isCurrentOrFutureQuarter(option.value));

/** @deprecated Use getPurchaseTimingOptions(labels) instead */
export const purchaseTimingOptions = getPurchaseTimingOptions();

/**
 * Preferred contact method options
 * @param labels - i18n labels from Contentful
 */
export const getPreferredContactMethodOptions = (labels?: Labels) => [
  { label: labels?.['const.preferredContactMethodOptions.email'] ?? 'Email', value: 'email' },
  { label: labels?.['const.preferredContactMethodOptions.phone'] ?? 'Phone', value: 'phone' },
  { label: labels?.['const.preferredContactMethodOptions.textsms'] ?? 'Text/SMS', value: 'text' },
];

/** @deprecated Use getPreferredContactMethodOptions(labels) instead */
export const preferredContactMethodOptions = getPreferredContactMethodOptions();

export type ExportHistoryLog = {
  id: string;
  fileNameOriginal?: string;
  exportedAt: string;
  exporterName: string;
};

export const PROSPECTS_EXPORT_TYPE_INCREMENTAL = 'INCREMENTAL';
export const PROSPECTS_EXPORT_TYPE_ALL = 'ALL';
export const PROSPECTS_EXPORT_TYPE_SEARCH = 'SEARCH';

export const EXPORT_MODE_INCREMENTAL = 'incremental';
export const EXPORT_MODE_ALL = 'all';
export const EXPORT_MODE_SEARCH = 'search';

export const MOCK_EXPORT_HISTORY_LOGS: ExportHistoryLog[] = [
  { id: '1', exportedAt: '2026-03-04T10:15:00Z', exporterName: 'Liam Vermeulen' },
  { id: '2', exportedAt: '2026-02-28T09:30:00Z', exporterName: 'Louis Van den Berghe' },
  { id: '3', exportedAt: '2026-02-22T15:45:00Z', exporterName: 'Julien Van Damme' },
  { id: '4', exportedAt: '2026-02-13T08:00:00Z', exporterName: 'Noah Peeters' },
  { id: '5', exportedAt: '2026-01-26T13:20:00Z', exporterName: 'Louis Van den Berghe' },
  { id: '6', exportedAt: '2026-01-18T11:05:00Z', exporterName: 'Arthur Dupont' },
  { id: '7', exportedAt: '2026-01-11T17:10:00Z', exporterName: 'Julien Van Damme' },
  { id: '8', exportedAt: '2026-01-02T07:40:00Z', exporterName: 'Julien Van Damme' },
];

/**
 * Email consent options
 * @param labels - i18n labels from Contentful
 */
export const getEmailConsentOptions = (labels?: Labels) => [
  { label: labels?.['const.emailConsentOptions.optIn'] ?? 'Opt In', value: 'emailOptIn' },
];

/** @deprecated Use getEmailConsentOptions(labels) instead */
export const emailConsentOptions = getEmailConsentOptions();

/**
 * Phone consent options
 * @param labels - i18n labels from Contentful
 */
export const getPhoneConsentOptions = (labels?: Labels) => [
  { label: labels?.['const.phoneConsentOptions.optIn'] ?? 'Opt In', value: 'callOptIn' },
  { label: labels?.['const.phoneConsentOptions.optOut'] ?? 'Opt Out', value: 'callOptOut' },
];

/** @deprecated Use getPhoneConsentOptions(labels) instead */
export const phoneConsentOptions = getPhoneConsentOptions();

/**
 * Badge shown next to the Email Consent field/column.
 * @param prospect - The prospect record
 * @param labels - i18n labels from Contentful
 */
export const getEmailConsentBadge = (
  prospect: Prospect | undefined,
  labels?: Labels
): BadgeProps => {
  const notCaptured = {
    label: labels?.['form.notCaptured'] ?? 'Not Captured',
    color: 'red' as const,
    type: 'secondary' as const,
  };

  if (prospect?.isEmailOptOut) {
    return { label: labels?.['form.optedOut'] ?? 'Opted Out', color: 'red', type: 'secondary' };
  }
  if (prospect?.isEmailOptIn == null) {
    return notCaptured;
  }
  if (prospect.isEmailOptIn && prospect.isEmailDoubleOptIn) {
    return {
      label: labels?.['form.doubleOptedIn'] ?? 'Double Opted In',
      color: 'green',
      type: 'secondary',
    };
  }
  if (prospect.isEmailOptIn && !prospect.isEmailDoubleOptIn) {
    return {
      label: labels?.['form.doubleOptInPending'] ?? 'Double Opt In Pending',
      color: 'orange',
      type: 'secondary',
    };
  }
  return notCaptured;
};

/**
 * Badge shown next to the Call/Phone Consent field/column.
 * @param prospect - The prospect record
 * @param labels - i18n labels from Contentful
 */
export const getCallConsentBadge = (
  prospect: Prospect | undefined,
  labels?: Labels
): BadgeProps => {
  if (prospect?.isCallOptIn) {
    return { label: labels?.['form.optedIn'] ?? 'Opted In', color: 'green', type: 'secondary' };
  }
  if (prospect?.isCallOptOut) {
    return { label: labels?.['form.optedOut'] ?? 'Opted Out', color: 'red', type: 'secondary' };
  }
  return {
    label: labels?.['form.notCaptured'] ?? 'Not Captured',
    color: 'red',
    type: 'secondary',
  };
};

/**
 * Test drive time slot options
 */
export const testDriveTimeOptions = [
  { label: '9:00 - 9:30', value: '9:00-9:30' },
  { label: '9:30 - 10:00', value: '9:30-10:00' },
  { label: '10:00 - 10:30', value: '10:00-10:30' },
  { label: '10:30 - 11:00', value: '10:30-11:00' },
  { label: '11:00 - 11:30', value: '11:00-11:30' },
  { label: '11:30 - 12:00', value: '11:30-12:00' },
  { label: '12:00 - 12:30', value: '12:00-12:30' },
  { label: '12:30 - 13:00', value: '12:30-13:00' },
  { label: '13:00 - 13:30', value: '13:00-13:30' },
  { label: '13:30 - 14:00', value: '13:30-14:00' },
  { label: '14:00 - 14:30', value: '14:00-14:30' },
  { label: '14:30 - 15:00', value: '14:30-15:00' },
  { label: '15:00 - 15:30', value: '15:00-15:30' },
  { label: '15:30 - 16:00', value: '15:30-16:00' },
  { label: '16:00 - 16:30', value: '16:00-16:30' },
  { label: '16:30 - 17:00', value: '16:30-17:00' },
];

/**
 * Test drive status options
 * @param labels - i18n labels from Contentful
 * @param isExistingTestDrive - Adds outcomes that only make sense once a test drive exists
 * @param currentStatus - The saved status; once past Requested, a test drive can't go back to it
 */
export const getStatusOptions = (
  labels?: Labels,
  isExistingTestDrive = false,
  currentStatus?: string
) => [
  ...(!currentStatus || currentStatus.toLowerCase() === 'requested'
    ? [{ label: labels?.['const.statusOptions.requested'] ?? 'Requested', value: 'requested' }]
    : []),
  { label: labels?.['const.statusOptions.scheduled'] ?? 'Scheduled', value: 'scheduled' },
  ...(isExistingTestDrive
    ? [
        { label: labels?.['const.statusOptions.noShow'] ?? 'No Show', value: 'no show' },
        { label: labels?.['const.statusOptions.cancelled'] ?? 'Cancelled', value: 'cancelled' },
      ]
    : []),
];

/** @deprecated Use getStatusOptions(labels) instead */
export const statusOptions = getStatusOptions();

/** Final outcomes whose status can no longer be changed. */
export const LOCKED_TEST_DRIVE_STATUSES = ['no show', 'cancelled'];

const ACTIVITY_TYPE_LABEL_KEYS: Record<ProspectActivityType, string> = {
  [ProspectActivityType.AGENCY_CAPTURE_FORM]: 'const.activityType.agencyCaptureForm',
  [ProspectActivityType.APPOINTMENT_AT_HOME]: 'const.activityType.appointmentAtHome',
  [ProspectActivityType.APPOINTMENT_REQUEST]: 'const.activityType.appointmentRequest',
  [ProspectActivityType.EVENT_LEAD_CAPTURE]: 'const.activityType.eventLeadCapture',
  [ProspectActivityType.HQ_CAPTURE_FORM]: 'const.activityType.hqCaptureForm',
  [ProspectActivityType.LINKEDIN_LEAD_CAPTURE]: 'const.activityType.linkedinLeadCapture',
  [ProspectActivityType.META_LEAD_CAPTURE]: 'const.activityType.metaLeadCapture',
  [ProspectActivityType.NEWSLETTER_SUBSCRIBE_FORM]: 'const.activityType.newsletterSubscribeForm',
  [ProspectActivityType.TEST_DRIVE_FORM]: 'const.activityType.testDriveForm',
  [ProspectActivityType.SHARE_DESIGN_FORM]: 'const.activityType.shareDesignForm',
  [ProspectActivityType.CONTACT_US]: 'const.activityType.contactUs',
  [ProspectActivityType.GENERAL_CONTACT_FORM]: 'const.activityType.generalContactForm',
  [ProspectActivityType.LOCATION_CHANGED]: 'const.activityType.locationChanged',
};

/**
 * Translated name for a prospect activity type.
 * Returns `fallback` for unknown types and for locales without a translation.
 * @param activityType - The activity type code from the API
 * @param labels - i18n labels from Contentful
 * @param fallback - What the caller shows today when there's no translation
 */
export const getActivityTypeLabel = (
  activityType: string | undefined,
  labels: Labels,
  fallback: string
): string => {
  const key = activityType
    ? ACTIVITY_TYPE_LABEL_KEYS[activityType as ProspectActivityType]
    : undefined;
  return (key && labels?.[key]) || fallback;
};

/**
 * Closed status sub status options
 */
export const getClosedSubStatusOptions = (labels?: Labels) => [
  { label: labels?.['const.closedSubStatus.junk'] ?? 'Junk', value: 'junk' },
  {
    label: labels?.['const.closedSubStatus.notInterested'] ?? 'Not Interested',
    value: 'not_interested',
  },
];

/** @deprecated Use getClosedSubStatusOptions(labels) instead */
export const closedSubStatusOptions = getClosedSubStatusOptions();

/**
 * Primary reason options for "Junk" sub status
 */
export const getJunkPrimaryReasons = (labels?: Labels) => [
  {
    label: labels?.['const.primaryReason.invalidEmail'] ?? 'Invalid Email',
    value: 'invalid_email',
  },
];

/** @deprecated Use getJunkPrimaryReasons(labels) instead */
export const junkPrimaryReasons = getJunkPrimaryReasons();

/**
 * Primary reason options for "Not Interested" sub status
 */
export const getNotInterestedPrimaryReasons = (labels?: Labels) => [
  { label: labels?.['const.primaryReason.charging'] ?? 'Charging', value: 'charging' },
  {
    label: labels?.['const.primaryReason.confidenceInCompany'] ?? 'Confidence in Company',
    value: 'confidence_in_company',
  },
  {
    label: labels?.['const.primaryReason.customerExperience'] ?? 'Customer Experience',
    value: 'customer_experience',
  },
  {
    label: labels?.['const.primaryReason.environmental'] ?? 'Environmental',
    value: 'environmental',
  },
  { label: labels?.['const.primaryReason.financing'] ?? 'Financing', value: 'financing' },
  {
    label:
      labels?.['const.primaryReason.purchasedDifferentVehicle'] ?? 'Purchased a Different Vehicle',
    value: 'purchased_a_different_vehicle',
  },
  {
    label: labels?.['const.primaryReason.serviceNetwork'] ?? 'Service Network',
    value: 'service_network',
  },
  { label: labels?.['const.primaryReason.timing'] ?? 'Timing', value: 'timing' },
  {
    label: labels?.['const.primaryReason.vehicleFeatures'] ?? 'Vehicle Features',
    value: 'vehicle_features',
  },
];

/** @deprecated Use getNotInterestedPrimaryReasons(labels) instead */
export const notInterestedPrimaryReasons = getNotInterestedPrimaryReasons();

/**
 * Secondary reason options mapped by primary reason
 */
export const getSecondaryReasonsByPrimary = (
  labels?: Labels
): Record<string, Array<{ label: string; value: string }>> => ({
  // Charging (Not Interested)
  charging: [
    {
      label:
        labels?.['const.secondaryReason.highInstallationCost'] ??
        'High Installation Cost for Home Charging',
      value: 'high_installation_cost_for_home_charging',
    },
    {
      label:
        labels?.['const.secondaryReason.atHomeChargingAvailability'] ??
        'At home charging availability',
      value: 'at_home_charging_availability',
    },
    {
      label:
        labels?.['const.secondaryReason.noBiDirectionalCharging'] ?? 'No Bi-directional charging',
      value: 'no_bi_directional_charging',
    },
    {
      label: labels?.['const.secondaryReason.preferNacsCharging'] ?? 'Prefer NACS charging',
      value: 'prefer_nacs_charging',
    },
    {
      label:
        labels?.['const.secondaryReason.unsatisfactoryChargingInfrastructure'] ??
        'Unsatisfactory Charging Infrastructure',
      value: 'unsatisfactory_charging_infrastructure',
    },
  ],
  // Confidence in Company (Not Interested)
  confidence_in_company: [
    {
      label:
        labels?.['const.secondaryReason.companyStockPerformance'] ?? 'Company Stock Performance',
      value: 'company_stock_performance',
    },
    {
      label: labels?.['const.secondaryReason.companyViability'] ?? 'Company viability',
      value: 'company_viability',
    },
    {
      label:
        labels?.['const.secondaryReason.concernsLongTermProductSupport'] ??
        'Concerns with Long term product support',
      value: 'concerns_with_long_term_product_support',
    },
    {
      label: labels?.['const.secondaryReason.productQualityConcerns'] ?? 'Product Quality Concerns',
      value: 'product_quality_concerns',
    },
  ],
  // Customer Experience (Not Interested)
  customer_experience: [
    {
      label:
        labels?.['const.secondaryReason.negativeSocialMediaReviewPost'] ??
        'Negative Social Media Review/Post',
      value: 'negative_social_media_review_post',
    },
    {
      label:
        labels?.['const.secondaryReason.poorExperienceInStudio'] ?? 'Poor Experience in Studio',
      value: 'poor_experience_in_studio',
    },
    {
      label:
        labels?.['const.secondaryReason.poorTestDriveExperience'] ?? 'Poor Test Drive Experience',
      value: 'poor_test_drive_experience',
    },
    {
      label:
        labels?.['const.secondaryReason.salesSpecialistNoDetailedInfo'] ??
        'Sales Specialist could not provide detailed information requested',
      value: 'sales_specialist_could_not_provide_detailed_information_requested',
    },
    {
      label:
        labels?.['const.secondaryReason.salesSpecialistUnresponsive'] ??
        'Sales Specialist Unresponsive',
      value: 'sales_specialist_unresponsive',
    },
    {
      label:
        labels?.['const.secondaryReason.unableTestDriveAppointment'] ??
        'Unable to get a Test Drive Appointment',
      value: 'unable_to_get_a_test_drive_appointment',
    },
  ],
  // Environmental (Not Interested)
  environmental: [
    {
      label: labels?.['const.secondaryReason.lackOfTaxRelief'] ?? 'Lack of Tax relief',
      value: 'lack_of_tax_relief',
    },
    {
      label: labels?.['const.secondaryReason.politicalUncertainty'] ?? 'Political uncertainty',
      value: 'political_uncertainty',
    },
  ],
  // Financing (Not Interested)
  financing: [
    {
      label:
        labels?.['const.secondaryReason.businessFinancingNotAvailable'] ??
        'Business Financing not available',
      value: 'business_financing_not_available',
    },
    {
      label: labels?.['const.secondaryReason.downpaymentTooHigh'] ?? 'Downpayment too High',
      value: 'downpayment_too_high',
    },
    {
      label:
        labels?.['const.secondaryReason.lackOfSufficientDocsForStipulations'] ??
        'Lack of Sufficient docs for stipulations',
      value: 'lack_of_sufficient_docs_for_stipulations',
    },
    {
      label: labels?.['const.secondaryReason.leasingNotAvailable'] ?? 'Leasing not available',
      value: 'leasing_not_available',
    },
    {
      label: labels?.['const.secondaryReason.paymentTooHigh'] ?? 'Payment Too High',
      value: 'payment_too_high',
    },
    {
      label:
        labels?.['const.secondaryReason.proposedRateOfferedIsTooHigh'] ??
        'Proposed Rate offered is too high',
      value: 'proposed_rate_offered_is_too_high',
    },
    {
      label:
        labels?.['const.secondaryReason.unavailablePurchaseOption'] ??
        'Unavailable purchase option',
      value: 'unavailable_purchase_option',
    },
    {
      label:
        labels?.['const.secondaryReason.unfavorableFinancingTerms'] ??
        'Unfavorable financing terms',
      value: 'unfavorable_financing_terms',
    },
  ],
  // Purchased a Different Vehicle (Not Interested)
  purchased_a_different_vehicle: [
    { label: labels?.['const.secondaryReason.otherEv'] ?? 'Other EV', value: 'other_ev' },
  ],
  // Service Network (Not Interested)
  service_network: [
    {
      label:
        labels?.['const.secondaryReason.limitedPhysicalLocations'] ?? 'Limited Physical Locations',
      value: 'limited_physical_locations',
    },
    {
      label: labels?.['const.secondaryReason.notPresentInMarket'] ?? 'Not Present in market',
      value: 'not_present_in_market',
    },
    {
      label:
        labels?.['const.secondaryReason.outOfMobileServiceLocations'] ??
        'Out of Mobile Service Locations',
      value: 'out_of_mobile_service_locations',
    },
    {
      label: labels?.['const.secondaryReason.warrantyConcerns'] ?? 'Warranty concerns',
      value: 'warranty_concerns',
    },
  ],
  // Timing (Not Interested)
  timing: [
    {
      label: labels?.['const.secondaryReason.extendingCurrentLease'] ?? 'Extending Current Lease',
      value: 'extending_current_lease',
    },
    {
      label: labels?.['const.secondaryReason.needVehicleSooner'] ?? 'Need a vehicle sooner',
      value: 'need_a_vehicle_sooner',
    },
    {
      label:
        labels?.['const.secondaryReason.remainingInCurrentVehicle'] ??
        'Remaining in Current Vehicle',
      value: 'remaining_in_current_vehicle',
    },
    {
      label: labels?.['const.secondaryReason.notReadyToEngage'] ?? 'Not ready to engage',
      value: 'not_ready_to_engage',
    },
  ],
  // Vehicle Features (Not Interested)
  vehicle_features: [
    {
      label: labels?.['const.secondaryReason.autonomousDriving'] ?? 'Autonomous Driving',
      value: 'autonomous_driving',
    },
    {
      label: labels?.['const.secondaryReason.batteryDegradation'] ?? 'Battery degradation',
      value: 'battery_degradation',
    },
    {
      label: labels?.['const.secondaryReason.colourOptions'] ?? 'Colour options',
      value: 'colour_options',
    },
    {
      label: labels?.['const.secondaryReason.comfortAccessibility'] ?? 'Comfort/Accessibility',
      value: 'comfort_accessibility',
    },
    {
      label: labels?.['const.secondaryReason.headUpDisplay'] ?? 'Head-up display',
      value: 'head_up_display',
    },
    { label: labels?.['const.secondaryReason.range'] ?? 'Range', value: 'range' },
    { label: labels?.['const.secondaryReason.safety'] ?? 'Safety', value: 'safety' },
    {
      label: labels?.['const.secondaryReason.securityFeatures'] ?? 'Security Features',
      value: 'security_features',
    },
    { label: labels?.['const.secondaryReason.suspension'] ?? 'Suspension', value: 'suspension' },
    { label: labels?.['const.secondaryReason.towing'] ?? 'Towing', value: 'towing' },
    {
      label: labels?.['const.secondaryReason.utilityStorage'] ?? 'Utility & Storage',
      value: 'utility_storage',
    },
    {
      label: labels?.['const.secondaryReason.softwareFeatures'] ?? 'Software features',
      value: 'software_features',
    },
  ],
});

/** @deprecated Use getSecondaryReasonsByPrimary(labels) instead */
export const secondaryReasonsByPrimary = getSecondaryReasonsByPrimary();

/**
 * Sub statuses that require primary/secondary reasons
 */
export const subStatusesWithReasons = ['junk', 'not_interested'];

/**
 * Primary reasons that require secondary reasons
 */
export const primaryReasonsWithSecondary = [
  'charging',
  'confidence_in_company',
  'customer_experience',
  'environmental',
  'financing',
  'purchased_a_different_vehicle',
  'service_network',
  'timing',
  'vehicle_features',
];
